import { buildQuizTaskSubmission, formatLmsQuizScore, quizAnswerKeyIsTrustworthy, resolveQuizQuestionResult } from 'pollworker/src/fetchers';
import { useMemo, useState } from 'react';
import { Button } from 'shared/src/components/ui/new';
import type { LmsAssignment, LmsAssignmentSubmitTask, LmsQuizQuestion } from 'shared/src/types/lms';
import { LmsAssignmentType, LmsGradingType, LmsQuizStatus } from 'shared/src/utils/lmsStatus';

function answerLetter(index: number): string {
  return String.fromCharCode('A'.charCodeAt(0) + index);
}

function hasAnySelection(selectedByTask: Record<string, Record<string, string[]>>) {
  return Object.values(selectedByTask).some((selectedByQuestion) => Object.values(selectedByQuestion).some((ids) => ids.length > 0));
}

function attemptsLeftLabel(maxRetries: number | null, attemptCount: number) {
  if (maxRetries == null || maxRetries <= 0) {
    return null;
  }

  const remaining = Math.max(Math.floor(maxRetries) - attemptCount, 0);
  if (remaining === 0) {
    return 'No attempts left';
  }

  return remaining === 1 ? '1 attempt left' : `${remaining} attempts left`;
}

export function formatAssignmentAdorners(
  assignment: Pick<LmsAssignment, 'Status' | 'Grade' | 'MaxGrade' | 'GradingType' | 'PassThresholdPercentage' | 'MaxRetries' | 'AttemptCount'>,
) {
  const scoreLabel = formatLmsQuizScore(assignment);
  const attemptsLeft = attemptsLeftLabel(assignment.MaxRetries, assignment.AttemptCount);

  return [
    assignment.Status === LmsQuizStatus.Graded ? `Score: ${scoreLabel}` : scoreLabel,
    assignment.PassThresholdPercentage !== null ? `Pass ${assignment.PassThresholdPercentage}%` : null,
    attemptsLeft,
  ]
    .filter(Boolean)
    .join(' · ');
}

function initialSelections(assignment: LmsAssignment): Record<string, Record<string, string[]>> {
  return Object.fromEntries(assignment.Tasks.map((task) => [task.TaskUuid, { ...task.SelectedAnswerIdsByQuestion }]));
}

export default function LmsAssignmentQuiz({
  assignment,
  submitting,
  retrying,
  error,
  onSubmit,
  onRetry,
}: {
  assignment: LmsAssignment;
  submitting: boolean;
  retrying: boolean;
  error: string | null;
  onSubmit: (tasks: LmsAssignmentSubmitTask[]) => void;
  onRetry: () => void;
}) {
  const quizTasks = useMemo(() => assignment.Tasks.filter((task) => task.AssignmentType === LmsAssignmentType.QUIZ), [assignment.Tasks]);
  const unsupportedTasks = useMemo(() => assignment.Tasks.filter((task) => task.AssignmentType !== LmsAssignmentType.QUIZ), [assignment.Tasks]);
  const locked = assignment.Status === LmsQuizStatus.Submitted || assignment.Status === LmsQuizStatus.Graded;
  const showResults = assignment.Status === LmsQuizStatus.Graded;
  const savedSelections = useMemo(() => initialSelections(assignment), [assignment]);
  const [draftSelections, setDraftSelections] = useState(() => initialSelections(assignment));
  const selectedByTask = locked ? savedSelections : draftSelections;

  const canSubmit = !locked && quizTasks.length > 0 && hasAnySelection(selectedByTask) && !submitting;

  function toggleAnswer(taskUuid: string, questionId: string, answerId: string) {
    if (locked || submitting) {
      return;
    }

    setDraftSelections((current) => {
      const selected = new Set(current[taskUuid]?.[questionId] ?? []);
      if (selected.has(answerId)) {
        selected.delete(answerId);
      } else {
        selected.add(answerId);
      }

      return {
        ...current,
        [taskUuid]: {
          ...(current[taskUuid] ?? {}),
          [questionId]: [...selected],
        },
      };
    });
  }

  async function handleSubmit() {
    if (!canSubmit) {
      return;
    }

    const quizTasksFormatted = await Promise.all(
      quizTasks.map((task) =>
        buildQuizTaskSubmission(task.Questions, selectedByTask[task.TaskUuid] ?? {}).then((TaskSubmission) => ({
          TaskUuid: task.TaskUuid,
          TaskSubmission,
        })),
      ),
    );

    onSubmit(quizTasksFormatted);
  }

  return (
    <div className="space-y-5">
      {assignment.OverallFeedback ? <p className="text-sm text-gray-700">{assignment.OverallFeedback}</p> : null}

      {unsupportedTasks.map((task) => (
        <div key={task.TaskUuid} className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {task.Title} is a {task.AssignmentType} task and is not supported here.
        </div>
      ))}

      {quizTasks.length === 0 ? <div className="text-sm text-gray-500">This assignment has no scored quiz questions.</div> : null}

      {quizTasks.map((task) => (
        <section key={task.TaskUuid} className="rounded-md border border-gray-200 bg-gray-50 p-4">
          <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">{task.Title}</div>
          {task.Description ? <p className="mb-3 text-sm text-gray-700">{task.Description}</p> : null}
          {task.Questions.map((question, questionIndex) => {
            const assignmentFailed = assignment.Grade !== null && assignment.Grade <= 0;
            const assignmentPerfect =
              assignment.Grade !== null &&
              (assignment.MaxGrade ?? (assignment.GradingType === LmsGradingType.PERCENTAGE || assignment.GradingType === LmsGradingType.PASS_FAIL ? 100 : null)) !== null
                ? assignment.Grade >= (assignment.MaxGrade ?? 100)
                : false;
            const useAssignmentGrade = task.Questions.length === 1 || assignmentFailed || assignmentPerfect;
            return (
              <QuestionOptions
                key={question.questionId}
                question={question}
                questionIndex={questionIndex}
                selected={new Set(selectedByTask[task.TaskUuid]?.[question.questionId] ?? [])}
                locked={locked || submitting}
                showResults={showResults}
                grade={task.TaskGrade ?? (useAssignmentGrade ? assignment.Grade : null)}
                maxGrade={task.TaskGrade !== null ? task.MaxGradeValue : (assignment.MaxGrade ?? task.MaxGradeValue)}
                gradingType={assignment.GradingType}
                onToggle={(answerId) => toggleAnswer(task.TaskUuid, question.questionId, answerId)}
              />
            );
          })}
        </section>
      ))}

      {error ? <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}

      <div className="flex flex-wrap gap-2">
        {locked || quizTasks.length === 0 ? null : (
          <Button type="button" size="sm" disabled={!canSubmit} onClick={handleSubmit}>
            {submitting ? 'Submitting…' : 'Submit quiz'}
          </Button>
        )}
        {assignment.CanRetry ? (
          <Button type="button" size="sm" variant="outline" disabled={retrying} onClick={onRetry}>
            {retrying ? 'Starting retry…' : 'Retry quiz'}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function QuestionOptions({
  question,
  questionIndex,
  selected,
  locked,
  showResults,
  grade,
  maxGrade,
  gradingType,
  onToggle,
}: {
  question: LmsQuizQuestion;
  questionIndex: number;
  selected: Set<string>;
  locked: boolean;
  showResults: boolean;
  grade: number | null;
  maxGrade: number | null;
  gradingType: string;
  onToggle: (answerId: string) => void;
}) {
  const result = showResults ? resolveQuizQuestionResult(question, selected, grade, maxGrade, gradingType) : 'unknown';
  const revealMissedKey = showResults && result === 'incorrect' && quizAnswerKeyIsTrustworthy(question, selected, grade, maxGrade, gradingType);

  return (
    <fieldset className="mb-4 last:mb-0" disabled={locked}>
      <legend className="mb-2 flex flex-wrap items-center gap-2 text-sm font-medium">
        <span>
          {questionIndex + 1}. {question.question.trim() || 'Untitled question'}
        </span>
        {result === 'correct' ? <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-semibold text-emerald-800">Correct</span> : null}
        {result === 'incorrect' ? <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-800">Incorrect</span> : null}
      </legend>
      {question.answers.length === 0 ? (
        <div className="text-sm text-gray-500">No answers provided.</div>
      ) : (
        <ul className="space-y-1.5">
          {question.answers.map((answer, answerIndex) => {
            const isSelected = selected.has(answer.answerId);
            const isMissedKey = revealMissedKey && !isSelected && answer.correct === true;
            const isWrongSelection = showResults && isSelected && result === 'incorrect';
            const isCorrectSelection = showResults && isSelected && result === 'correct';
            const letter = answerLetter(answerIndex);

            let rowClass = 'border-gray-200 bg-white hover:bg-gray-50';
            let chipClass = 'bg-gray-100 text-gray-600';
            let textClass = 'text-gray-800';
            let hint: string | null = null;

            if (!showResults && isSelected) {
              rowClass = 'border-blue-300 bg-blue-50';
              chipClass = 'bg-blue-500 text-white';
            }
            if (showResults && isCorrectSelection) {
              rowClass = 'border-emerald-300 bg-emerald-50';
              chipClass = 'bg-emerald-500 text-white';
              textClass = 'font-medium text-emerald-900';
              hint = 'Your answer';
            }
            if (isMissedKey) {
              rowClass = 'border-emerald-300 bg-emerald-50';
              chipClass = 'bg-emerald-500 text-white';
              textClass = 'font-medium text-emerald-900';
              hint = 'Correct answer';
            }
            if (isWrongSelection) {
              rowClass = 'border-red-300 bg-red-50';
              chipClass = 'bg-red-500 text-white';
              textClass = 'font-medium text-red-900';
              hint = 'Your answer';
            }
            if (showResults && !isSelected && !isMissedKey) {
              rowClass = 'border-gray-200 bg-white opacity-60';
              chipClass = 'bg-gray-100 text-gray-400';
              textClass = 'text-gray-500';
            }

            return (
              <li key={answer.answerId}>
                <button
                  type="button"
                  aria-pressed={isSelected}
                  disabled={locked}
                  onClick={() => onToggle(answer.answerId)}
                  className={`flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left text-sm transition-colors disabled:cursor-default ${rowClass}`}
                >
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded text-[11px] font-bold ${chipClass}`}>
                    {isCorrectSelection || isMissedKey ? '✓' : isWrongSelection ? '✕' : letter}
                  </span>
                  <span className={textClass}>{answer.text.trim() || 'Untitled answer'}</span>
                  {hint ? <span className="ml-auto shrink-0 text-xs font-medium">{hint}</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </fieldset>
  );
}
